import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, ExternalLink, File as FileIcon, FileText, Film, Folder, FolderPlus, Link2, Play, Plus, Trash2, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtD } from "./gUtils";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const driveFileUrl = (id, dl = 0) => `${BACKEND}/api/graphisme/drive/files/${id}/file${dl ? "?dl=1" : ""}`;
const zipUrl = (fid, ids) => `${BACKEND}/api/graphisme/drive/folders/${fid}/zip${ids?.length ? `?ids=${ids.join(",")}` : ""}`;

// Le portail client signale son rôle : un navigateur peut porter les cookies
// agence ET client (ex. l'agence teste l'espace client) — sans cette indication,
// le backend résoudrait l'agence en premier et les appels client échoueraient.
const hdrs = (side) => (side === "client" ? { headers: { "x-espace-role": "client" } } : {});

const kindOf = (d) =>
  d.external_url ? "link"
    : (d.content_type || "").startsWith("image/") ? "image"
    : (d.content_type || "").startsWith("video/") ? "video"
    : d.content_type === "application/pdf" ? "pdf"
    : "file";

const fmtSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} o`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1048576).toFixed(1)} Mo`;
};

const canDeleteFile = (d, side) => side === "agency" || d.direction === "client_to_agency";
const canDeleteFolder = (f, side) => side === "agency" || f.created_by_role === "client";
const linkDomain = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return "Lien externe"; } };

// ---------------------------------------------------------------- carte fichier

function FileCard({ d, side, selected, onToggle, onOpen, onChanged }) {
  const kind = kindOf(d);
  const isNew = side === "agency" ? !d.seen_by_agency : !d.seen_by_client;
  const nameParts = (d.file_name || "").split(".");
  const ext = nameParts.length > 1 ? nameParts.pop().toUpperCase() : "";
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer « ${d.title} » ?`)) return;
    try { await api.delete(`/graphisme/drive/files/${d.id}`, hdrs(side)); toast.success("Fichier supprimé"); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Suppression impossible"); }
  };
  return (
    <div className={`group relative bg-white border rounded-xl overflow-hidden transition-all ${selected ? "border-neutral-900 ring-1 ring-neutral-900" : "border-neutral-200 hover:border-neutral-400"}`}
      data-testid={`drive-file-${d.id}`}>
      <button onClick={(e) => { e.stopPropagation(); onToggle(); }} aria-label="Sélectionner"
        className={`absolute top-2 left-2 z-10 w-5 h-5 rounded-md border flex items-center justify-center transition-all ${selected ? "bg-neutral-900 border-neutral-900 opacity-100" : "bg-white/90 border-neutral-300 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"}`}
        data-testid={`drive-file-select-${d.id}`}>
        {selected && <svg viewBox="0 0 10 8" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M1 4l2.5 2.5L9 1" /></svg>}
      </button>
      {isNew && <span className="absolute top-2 right-2 z-10 text-[9px] font-semibold uppercase tracking-wide bg-orange-500 text-white rounded-full px-2 py-0.5" data-testid={`drive-file-new-${d.id}`}>Nouveau</span>}
      <div className="aspect-square bg-neutral-100 cursor-pointer flex items-center justify-center overflow-hidden" onClick={onOpen} data-testid={`drive-file-open-${d.id}`}>
        {kind === "image" ? (
          <img src={driveFileUrl(d.id)} alt={d.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : kind === "video" ? (
          <div className="relative w-full h-full bg-neutral-900 flex items-center justify-center">
            <Film className="w-8 h-8 text-neutral-600" />
            <span className="absolute inset-0 flex items-center justify-center"><span className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow"><Play className="w-4 h-4 text-neutral-900 ml-0.5" /></span></span>
          </div>
        ) : kind === "pdf" ? (
          <div className="flex flex-col items-center gap-1.5 text-red-400"><FileText className="w-10 h-10" /><span className="text-[10px] font-bold tracking-widest">PDF</span></div>
        ) : kind === "link" ? (
          <div className="flex flex-col items-center gap-1.5 text-blue-500 px-3 text-center"><ExternalLink className="w-8 h-8" /><span className="text-[10px] font-medium text-blue-400 truncate max-w-full">{linkDomain(d.external_url)}</span></div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-neutral-400"><FileIcon className="w-10 h-10" />{ext && <span className="text-[10px] font-bold tracking-widest">{ext}</span>}</div>
        )}
      </div>
      <div className="px-2.5 py-2 border-t border-neutral-100">
        <p className="text-xs font-medium truncate" title={d.title}>{d.title}</p>
        <p className="text-[10px] text-neutral-400 truncate">{kind === "link" ? "Lien externe" : fmtSize(d.size)}{d.created_by ? ` · ${d.created_by}` : ""}</p>
      </div>
      <div className="absolute bottom-9 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {kind !== "link" && (
          <a href={driveFileUrl(d.id, 1)} onClick={(e) => e.stopPropagation()} title="Télécharger"
            className="w-6 h-6 rounded-md bg-white/95 border border-neutral-200 flex items-center justify-center text-neutral-500 hover:text-neutral-900 shadow-sm"
            data-testid={`drive-file-dl-${d.id}`}><Download className="w-3 h-3" /></a>
        )}
        {canDeleteFile(d, side) && (
          <button onClick={remove} title="Supprimer"
            className="w-6 h-6 rounded-md bg-white/95 border border-neutral-200 flex items-center justify-center text-neutral-500 hover:text-red-600 shadow-sm"
            data-testid={`drive-file-del-${d.id}`}><Trash2 className="w-3 h-3" /></button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- visionneuse

function Lightbox({ items, index, onClose, onNav }) {
  const d = items[index];
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onNav(index - 1);
      if (e.key === "ArrowRight" && index < items.length - 1) onNav(index + 1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [index, items.length, onClose, onNav]);
  if (!d) return null;
  const kind = kindOf(d);
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="drive-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm truncate flex-1">{d.title} <span className="text-neutral-400 text-xs ml-2">{index + 1} / {items.length}</span></p>
        <div className="flex items-center gap-1">
          <a href={driveFileUrl(d.id, 1)} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Télécharger" data-testid="drive-lightbox-dl"><Download className="w-4 h-4" /></a>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Fermer" data-testid="drive-lightbox-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-12 pb-4" onClick={(e) => e.stopPropagation()}>
        {index > 0 && (
          <button onClick={() => onNav(index - 1)} className="absolute left-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-testid="drive-lightbox-prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {kind === "video" ? (
          <video src={driveFileUrl(d.id)} controls autoPlay className="max-w-full max-h-full rounded-lg" data-testid="drive-lightbox-video" />
        ) : (
          <img src={driveFileUrl(d.id)} alt={d.title} className="max-w-full max-h-full object-contain rounded-lg" data-testid="drive-lightbox-img" />
        )}
        {index < items.length - 1 && (
          <button onClick={() => onNav(index + 1)} className="absolute right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-testid="drive-lightbox-next">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- ajout de contenu

export function AddContentModal({ open, onClose, side, clientId, folders, presetFolderId = "", onDone }) {
  const [mode, setMode] = useState("existing");
  const [folderId, setFolderId] = useState("");
  const [newName, setNewName] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(false);  // l'agence choisit explicitement de notifier
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (!open) return;
    setMode(presetFolderId ? "existing" : folders.length ? "existing" : "new");
    setFolderId(presetFolderId || folders[0]?.id || "");
    setNewName(""); setItems([]); setBusy(false); setNotify(false);
  }, [open, presetFolderId, folders]);

  const addFiles = (fs) => setItems((it) => [...it, ...Array.from(fs || []).map((f) => ({ key: `${f.name}-${f.size}-${Math.random().toString(36).slice(2)}`, file: f, progress: 0, status: "wait", error: "" }))]);
  const setItem = (key, patch) => setItems((it) => it.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const submit = async () => {
    const todo = items.filter((i) => i.status !== "done");
    if (!todo.length) { toast.error("Ajoutez au moins un fichier"); return; }
    if (mode === "new" && !newName.trim()) { toast.error("Nom du dossier requis"); return; }
    if (mode === "existing" && !folderId) { toast.error("Sélectionnez un dossier"); return; }
    setBusy(true);
    try {
      let fid = folderId;
      if (mode === "new") {
        const r = await api.post("/graphisme/drive/folders", { name: newName.trim(), client_id: side === "agency" ? clientId : undefined }, hdrs(side));
        fid = r.data.id;
      }
      let ok = items.length - todo.length;
      for (let i = 0; i < todo.length; i++) {
        const it = todo[i];
        setItem(it.key, { status: "up", error: "" });
        const fd = new FormData();
        fd.append("files", it.file);
        fd.append("notify", notify && i === todo.length - 1 ? "true" : "false");
        try {
          await api.post(`/graphisme/drive/folders/${fid}/files`, fd, {
            ...(side === "client" ? { headers: { "x-espace-role": "client" } } : {}),
            onUploadProgress: (e) => setItem(it.key, { progress: e.total ? Math.round((e.loaded / e.total) * 100) : 50 }),
          });
          setItem(it.key, { status: "done", progress: 100 });
          ok++;
        } catch (e) {
          setItem(it.key, { status: "err", error: e.response?.data?.detail || "Erreur d'envoi" });
        }
      }
      if (ok > 0) { toast.success(`${ok} fichier(s) importé(s)`); onDone?.(fid); }
      if (ok === items.length) onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="drive-add-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">Ajouter du contenu</DialogTitle>
        <div className="space-y-4 mt-2">
          {!presetFolderId && (
            <div className="space-y-2">
              <div className="flex gap-1.5" data-testid="drive-folder-mode">
                {[["new", "Créer un nouveau dossier"], ["existing", "Dossier existant"]].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setMode(id)} disabled={id === "existing" && folders.length === 0}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-xs border transition-colors disabled:opacity-40 ${mode === id ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-200 text-neutral-500 hover:text-neutral-900"}`}
                    data-testid={`drive-folder-mode-${id}`}>{label}</button>
                ))}
              </div>
              {mode === "new" ? (
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nom du dossier" data-testid="drive-new-folder-name" autoFocus />
              ) : (
                <select value={folderId} onChange={(e) => setFolderId(e.target.value)} data-testid="drive-folder-select"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white">
                  {(() => {
                    const byParent = {};
                    folders.forEach((f) => { (byParent[f.parent_id || ""] = byParent[f.parent_id || ""] || []).push(f); });
                    const rows = [];
                    const walk = (pid, depth) => (byParent[pid] || []).forEach((f) => {
                      const label = `${"— ".repeat(depth)}${f.name} (${f.file_count})`;
                      rows.push(<option key={f.id} value={f.id}>{label}</option>);
                      walk(f.id, depth + 1);
                    });
                    walk("", 0);
                    return rows;
                  })()}
                </select>
              )}
            </div>
          )}
          <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl px-4 py-8 text-center cursor-pointer transition-colors ${drag ? "border-neutral-900 bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`}
            data-testid="drive-dropzone">
            <Upload className="w-6 h-6 mx-auto text-neutral-400 mb-2" />
            <p className="text-sm text-neutral-600">Glissez vos fichiers ici</p>
            <p className="text-xs text-neutral-400 mt-0.5">ou <span className="underline">parcourir mes fichiers</span> — plusieurs fichiers possibles</p>
            <input ref={inputRef} type="file" multiple className="hidden" data-testid="drive-file-input"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          </div>
          {items.length > 0 && (
            <div className="space-y-1.5" data-testid="drive-upload-list">
              {items.map((it) => (
                <div key={it.key} className="text-xs" data-testid={`drive-upload-item-${it.key}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex-1 truncate">{it.file.name}</span>
                    {it.status === "done" ? <span className="text-green-600 font-medium shrink-0">100 %</span>
                      : it.status === "err" ? (
                        <button onClick={() => !busy && submit()} className="text-red-600 underline shrink-0" data-testid={`drive-upload-retry-${it.key}`}>Réessayer</button>
                      ) : <span className="text-neutral-400 shrink-0">{it.status === "up" ? `${it.progress} %` : "en attente"}</span>}
                    {it.status === "wait" && !busy && (
                      <button onClick={() => setItems((ls) => ls.filter((x) => x.key !== it.key))} className="text-neutral-300 hover:text-red-500 shrink-0" data-testid={`drive-upload-rm-${it.key}`}><X className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                  <div className="h-1 rounded-full bg-neutral-100 mt-1 overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${it.status === "err" ? "bg-red-400" : it.status === "done" ? "bg-green-500" : "bg-neutral-900"}`} style={{ width: `${it.progress}%` }} />
                  </div>
                  {it.error && <p className="text-[10px] text-red-500 mt-0.5">{it.error}</p>}
                </div>
              ))}
            </div>
          )}
          {side === "agency" && (
            <label className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer" data-testid="drive-notify-wrap">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="rounded border-neutral-300" data-testid="drive-notify-check" />
              Notifier le client par email
            </label>
          )}
          <Button onClick={submit} disabled={busy || items.every((i) => i.status === "done")}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="drive-upload-submit">
            <Upload className="w-4 h-4" /> {busy ? "Envoi en cours…" : `Importer ${items.filter((i) => i.status !== "done").length || ""} fichier(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- lien externe

function AddLinkModal({ open, onClose, side, folderId, onDone }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTitle(""); setUrl(""); } }, [open]);
  const submit = async () => {
    if (!title.trim()) { toast.error("Nom du lien requis"); return; }
    if (!/^https?:\/\//i.test(url.trim())) { toast.error("Lien invalide (http/https requis)"); return; }
    setBusy(true);
    try {
      await api.post(`/graphisme/drive/folders/${folderId}/link`, { title: title.trim(), url: url.trim(), notify: true }, hdrs(side));
      toast.success("Lien ajouté au dossier");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="drive-link-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">Ajouter un lien</DialogTitle>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Nom du lien *</Label>
            <Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex : Galerie photos, ressource web…" data-testid="drive-link-title" autoFocus />
          </div>
          <div>
            <Label className="text-xs">URL *</Label>
            <Input type="url" className="mt-1" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="drive-link-url" />
          </div>
          <Button onClick={submit} disabled={busy} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="drive-link-submit">
            <Link2 className="w-4 h-4" /> {busy ? "Ajout…" : "Ajouter le lien"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- explorateur principal

export function DriveExplorer({ side = "agency", clientId = "" }) {
  const [cid, setCid] = useState(clientId || "");
  const [clients, setClients] = useState([]);
  const [folders, setFolders] = useState(null);
  const [folder, setFolder] = useState(null);
  const [files, setFiles] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [lightbox, setLightbox] = useState(-1);
  const [addOpen, setAddOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParent, setNewFolderParent] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => { setCid(clientId || ""); }, [clientId]);
  useEffect(() => {
    if (side === "agency" && !clientId) api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch(() => {});
  }, [side, clientId]);

  const loadFolders = useCallback(async () => {
    if (side === "agency" && !cid) { setFolders(null); return; }
    try {
      const qs = side === "agency" ? `?client_id=${cid}` : "";
      const r = await api.get(`/graphisme/drive/folders${qs}`, hdrs(side));
      setFolders(r.data);
    } catch { setFolders([]); }
  }, [cid, side]);
  useEffect(() => { loadFolders(); }, [loadFolders]);

  const openFolder = async (f) => {
    setFolder(f); setFiles(null); setSelected(new Set());
    try {
      const r = await api.get(`/graphisme/drive/folders/${f.id}/files`, hdrs(side));
      setFiles(r.data);
      api.post(`/graphisme/drive/folders/${f.id}/seen`, {}, hdrs(side)).then(loadFolders).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible d'ouvrir le dossier"); setFolder(null); }
  };
  const reloadFiles = () => folder && api.get(`/graphisme/drive/folders/${folder.id}/files`, hdrs(side)).then((r) => setFiles(r.data)).catch(() => {});
  const refresh = () => { reloadFiles(); loadFolders(); };

  const createFolder = async () => {
    if (!newFolderName.trim()) { toast.error("Nom du dossier requis"); return; }
    setCreating(true);
    try {
      await api.post("/graphisme/drive/folders", { name: newFolderName.trim(), client_id: side === "agency" ? cid : undefined, parent_id: newFolderParent || undefined }, hdrs(side));
      toast.success(newFolderParent ? "Sous-dossier créé" : "Dossier créé");
      setNewFolderOpen(false); setNewFolderName(""); setNewFolderParent(null); loadFolders();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setCreating(false); }
  };

  const removeFolder = async (f) => {
    const msg = f.file_count > 0
      ? `Supprimer le dossier « ${f.name} » et ses ${f.file_count} fichier(s) ?`
      : `Supprimer le dossier vide « ${f.name} » ?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/graphisme/drive/folders/${f.id}`, hdrs(side)); toast.success("Dossier supprimé"); loadFolders(); }
    catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (s.size === (files || []).length ? new Set() : new Set((files || []).map((d) => d.id))));

  const deleteSelected = async () => {
    const list = (files || []).filter((d) => selected.has(d.id) && canDeleteFile(d, side));
    if (!list.length) { toast.error("Aucun fichier supprimable dans la sélection"); return; }
    if (!window.confirm(`Supprimer ${list.length} fichier(s) sélectionné(s) ?`)) return;
    let ok = 0;
    for (const d of list) { try { await api.delete(`/graphisme/drive/files/${d.id}`, hdrs(side)); ok++; } catch { /* ignore */ } }
    toast.success(`${ok} fichier(s) supprimé(s)`);
    setSelected(new Set()); refresh();
  };

  // ------------------------------------------------ sélection du client (agence, page globale)
  if (side === "agency" && !cid) {
    return (
      <div className="space-y-4" data-testid="drive-explorer">
        <select value="" onChange={(e) => e.target.value && setCid(e.target.value)} data-testid="drive-client-select"
          className="w-full max-w-sm border border-neutral-200 rounded-lg px-3 py-2.5 text-sm bg-white">
          <option value="">Sélectionner un client…</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="border border-dashed border-neutral-300 rounded-xl p-10 text-center text-sm text-neutral-400">
          Choisissez un client pour ouvrir son espace Documents partagé.
        </div>
      </div>
    );
  }

  const clientName = clients.find((c) => c.id === cid)?.name;

  // Carte dossier partagée entre la vue racine et l'intérieur d'un dossier (sous-dossiers)
  const renderFolderCard = (f) => (
    <div key={f.id} className="group relative bg-white border border-neutral-200 rounded-xl p-4 hover:border-neutral-400 transition-colors cursor-pointer"
      onClick={() => openFolder(f)} data-testid={`drive-folder-${f.id}`}>
      <div className="flex items-start gap-3">
        <Folder className="w-8 h-8 text-amber-500 fill-amber-100 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{f.name}</p>
          <p className="text-[11px] text-neutral-400 mt-0.5 truncate">
            {f.file_count} fichier(s)
            {f.last_modified ? ` · Modifié le ${fmtD(f.last_modified)}` : ""}
            {f.last_modified_by ? ` · par ${f.last_modified_by}` : ""}
          </p>
          {f.new_count > 0 && (
            <span className="inline-block mt-1.5 text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-2 py-0.5" data-testid={`drive-folder-new-${f.id}`}>
              {f.new_count} nouveau(x)
            </span>
          )}
        </div>
        {canDeleteFolder(f, side) && (
          <button onClick={(e) => { e.stopPropagation(); removeFolder(f); }} title="Supprimer le dossier"
            className="p-1.5 rounded-md text-neutral-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
            data-testid={`drive-folder-del-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </div>
    </div>
  );

  // ------------------------------------------------ vue dossier
  if (folder) {
    const subfolders = (folders || []).filter((f) => f.parent_id === folder.id);
    const parent = folder.parent_id ? (folders || []).find((f) => f.id === folder.parent_id) : null;
    const previewables = (files || []).filter((d) => ["image", "video"].includes(kindOf(d)));
    const openFile = (d) => {
      const kind = kindOf(d);
      if (kind === "link") { window.open(d.external_url, "_blank", "noreferrer"); return; }
      if (kind === "image" || kind === "video") { setLightbox(previewables.indexOf(d)); return; }
      window.open(driveFileUrl(d.id), "_blank", "noreferrer");
    };
    return (
      <div className="space-y-4" data-testid="drive-folder-view">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => { if (parent) { openFolder(parent); } else { setFolder(null); setFiles(null); loadFolders(); } }}
              className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors" data-testid="drive-back"
              title={parent ? `Retour à « ${parent.name} »` : "Retour aux dossiers"}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <h3 className="font-heading font-bold text-base truncate flex items-center gap-2"><Folder className="w-4 h-4 text-amber-500 shrink-0" /> {folder.name}</h3>
              <p className="text-[11px] text-neutral-400">{(files || []).length} fichier(s){folder.last_modified ? ` · modifié le ${fmtD(folder.last_modified)}` : ""}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={zipUrl(folder.id)} className="inline-flex items-center gap-1.5 text-xs border border-neutral-200 rounded-lg px-3 py-2 hover:border-neutral-400 transition-colors bg-white" data-testid="drive-download-all">
              <Download className="w-3.5 h-3.5" /> Tout télécharger
            </a>
            <Button size="sm" variant="outline" onClick={() => { setNewFolderParent(folder.id); setNewFolderOpen(true); }} className="gap-1.5 text-xs" data-testid="drive-new-subfolder">
              <FolderPlus className="w-3.5 h-3.5" /> Sous-dossier
            </Button>
            <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)} className="gap-1.5 text-xs" data-testid="drive-add-link">
              <Link2 className="w-3.5 h-3.5" /> Ajouter un lien
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white text-xs" data-testid="drive-add-content">
              <Plus className="w-3.5 h-3.5" /> Ajouter du contenu
            </Button>
          </div>
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-neutral-900 text-white rounded-xl px-4 py-2.5" data-testid="drive-selection-bar">
            <span className="text-xs font-medium" data-testid="drive-selected-count">{selected.size} fichier(s) sélectionné(s)</span>
            <div className="flex gap-2 ml-auto">
              <button onClick={toggleAll} className="text-xs text-neutral-300 hover:text-white transition-colors" data-testid="drive-select-all">
                {selected.size === (files || []).length ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
              <a href={zipUrl(folder.id, [...selected])} className="text-xs bg-white text-neutral-900 rounded-lg px-2.5 py-1 hover:bg-neutral-100 transition-colors" data-testid="drive-download-selected">Télécharger la sélection</a>
              {[...selected].some((id) => canDeleteFile((files || []).find((d) => d.id === id) || {}, side)) && (
                <button onClick={deleteSelected} className="text-xs text-red-300 hover:text-red-200 transition-colors" data-testid="drive-delete-selected">Supprimer</button>
              )}
            </div>
          </div>
        )}

        {subfolders.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="drive-subfolders-grid">
            {subfolders.map(renderFolderCard)}
          </div>
        )}

        {files === null ? (
          <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>
        ) : files.length === 0 && subfolders.length === 0 ? (
          <button onClick={() => setAddOpen(true)} className="w-full border border-dashed border-neutral-300 rounded-xl p-10 text-center text-sm text-neutral-400 hover:border-neutral-400 hover:text-neutral-600 transition-colors" data-testid="drive-folder-empty">
            Dossier vide — cliquez pour ajouter du contenu.
          </button>
        ) : files.length === 0 ? null : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="drive-files-grid">
            {files.map((d) => (
              <FileCard key={d.id} d={d} side={side} selected={selected.has(d.id)}
                onToggle={() => toggle(d.id)} onOpen={() => openFile(d)} onChanged={refresh} />
            ))}
          </div>
        )}

        <AddContentModal open={addOpen} onClose={() => setAddOpen(false)} side={side} clientId={cid}
          folders={folders || []} presetFolderId={folder.id} onDone={refresh} />
        <AddLinkModal open={linkOpen} onClose={() => setLinkOpen(false)} side={side} folderId={folder.id} onDone={refresh} />
        {lightbox >= 0 && <Lightbox items={previewables} index={lightbox} onClose={() => setLightbox(-1)} onNav={setLightbox} />}
      </div>
    );
  }

  // ------------------------------------------------ vue dossiers
  return (
    <div className="space-y-4" data-testid="drive-explorer">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {side === "agency" && !clientId && (
            <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="drive-client-select"
              className="border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white max-w-52">
              {clientName ? <option value={cid}>{clientName}</option> : <option value={cid}>Client</option>}
              {clients.filter((c) => c.id !== cid).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <p className="text-xs text-neutral-400">{folders ? `${folders.length} dossier(s)` : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { setNewFolderParent(null); setNewFolderOpen(true); }} className="gap-1.5 text-xs" data-testid="drive-new-folder">
            <FolderPlus className="w-3.5 h-3.5" /> Nouveau dossier
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white text-xs" data-testid="drive-add-content">
            <Plus className="w-3.5 h-3.5" /> Ajouter du contenu
          </Button>
        </div>
      </div>

      {folders === null ? (
        <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>
      ) : folders.length === 0 ? (
        <div className="border border-dashed border-neutral-300 rounded-xl p-10 text-center" data-testid="drive-empty">
          <Folder className="w-8 h-8 mx-auto text-neutral-300 mb-2" />
          <p className="text-sm text-neutral-400">Aucun dossier pour le moment.</p>
          <p className="text-xs text-neutral-400 mt-1">Créez un dossier puis ajoutez-y des fichiers, images, vidéos, PDF ou liens.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="drive-folders-grid">
          {folders.filter((f) => !f.parent_id).map(renderFolderCard)}
        </div>
      )}

      <AddContentModal open={addOpen} onClose={() => setAddOpen(false)} side={side} clientId={cid}
        folders={folders || []} onDone={(fid) => { const f = (folders || []).find((x) => x.id === fid); loadFolders(); if (f) openFolder({ ...f }); } } />

      <Dialog open={newFolderOpen} onOpenChange={(v) => !v && setNewFolderOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="drive-new-folder-modal" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold text-lg">{newFolderParent ? "Nouveau sous-dossier" : "Nouveau dossier"}</DialogTitle>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Nom du dossier *</Label>
              <Input className="mt-1" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Ex : Identité visuelle, Contenus campagne…" data-testid="drive-new-folder-input" autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") createFolder(); }} />
            </div>
            <Button onClick={createFolder} disabled={creating} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="drive-new-folder-submit">
              <FolderPlus className="w-4 h-4" /> {creating ? "Création…" : "Créer le dossier"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
